import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { Backdrop, Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { useMuiController } from '@zenput/util-common';
import { SimpleGallery } from '../../shared/SimpleGallery/SimpleGallery';
import {
    FollowUpActionFileType,
    FollowUpActionImageType,
    FollowUpActionType,
    ReplyFollowUpActionType,
    ZenputFileCollection
} from './FollowUpActions.types';
import {
    FollowUpActionsGallery,
    FollowUpActionsGalleryContainer,
    FollowUpActionsGalleryItem
} from './FollowUpActionsGallery';

interface FollowUpActionInfoProps {
    followUpAction: FollowUpActionType;
    getS3URL: (value: string) => string;
    onGoToLocationTaskPage: () => void;
    onGoToSource: () => void;
    uploadedReplyImages: FollowUpActionFileType[];
    savedReplyImages: FollowUpActionImageType[];
    onUploadReplyImages: (newImages: FollowUpActionFileType[]) => void;
    onDeleteUploadedImage: (indexToRemove: number) => void;
    uploadFilesToS3: (files: ZenputFileCollection) => Promise<(string | null)[]>;
    showSpinner: boolean;
    onLoadingChange: Dispatch<SetStateAction<boolean>>;
    noteField: ReturnType<typeof useMuiController<ReplyFollowUpActionType, 'note'>>['field'];
}

export const FollowUpActionInfo = (props: FollowUpActionInfoProps) => {
    const {
        followUpAction,
        getS3URL,
        onGoToLocationTaskPage,
        onGoToSource,
        uploadedReplyImages,
        savedReplyImages,
        onUploadReplyImages,
        onDeleteUploadedImage,
        uploadFilesToS3,
        showSpinner,
        onLoadingChange,
        noteField
    } = props;

    const {
        data: { description, images = [] },
        date_completed,
        location,
        reply_type,
        source_id,
        title
    } = followUpAction;

    const [galleryOpen, setGalleryOpen] = useState(false);
    const [startIndex, setStartIndex] = useState(0);

    const galleryItems = useMemo(
        () =>
            images?.map((s3Path, idx) => ({
                id: `${idx}-${s3Path}`,
                src: getS3URL(s3Path)
            })) ?? [],
        [images]
    );

    const openGallery = (index: number) => {
        setStartIndex(index);
        setGalleryOpen(true);
    };
    const closeGallery = () => setGalleryOpen(false);

    return (
        <>
            <Stack direction="column" bgcolor="white" marginTop={2} paddingX={2} paddingBottom={2}>
                {showSpinner && (
                    <Backdrop open sx={{ zIndex: (theme) => theme.zIndex.appBar - 1 }}>
                        <CircularProgress />
                    </Backdrop>
                )}
                {galleryOpen && galleryItems && (
                    <SimpleGallery items={galleryItems} onClose={closeGallery} startIndex={startIndex} />
                )}
                <div className="task_details">
                    <div className="card_body">
                        <h3>{title}</h3>

                        <div className="detail_items">
                            {location && (
                                <div className="detail_item task_account">
                                    <a className="view_task_account" href="#" onClick={onGoToLocationTaskPage}>
                                        <i className="df-location-filled" />
                                        {location.name}
                                    </a>
                                </div>
                            )}
                        </div>

                        {description && (
                            <div className="task_description">
                                <p className="task_description_text">{description}</p>
                            </div>
                        )}

                        {images && images.length > 0 && (
                            <FollowUpActionsGalleryContainer sx={(theme) => ({ mb: theme.spacing(2) })}>
                                {images.map((s3Path, index) => (
                                    <FollowUpActionsGalleryItem
                                        alt={`preview ${index}`}
                                        key={index}
                                        onClick={() => {
                                            openGallery(index);
                                        }}
                                        src={getS3URL(s3Path)}
                                    />
                                ))}
                            </FollowUpActionsGalleryContainer>
                        )}
                    </div>
                </div>

                {reply_type && reply_type == 'photo' && (
                    <Stack>
                        <FollowUpActionsGallery
                            getS3URL={getS3URL}
                            isReply
                            onDeleteUploadedImage={onDeleteUploadedImage}
                            onUploadFiles={onUploadReplyImages}
                            photoQualityAnalysis
                            readOnly={!!date_completed}
                            savedFiles={savedReplyImages}
                            uploadedFiles={uploadedReplyImages}
                            uploadFilesToS3={uploadFilesToS3}
                            onLoadingChange={onLoadingChange}
                        />
                        {!date_completed && (
                            <TextField
                                label={_t('mobile_field_textarea_placeholder_note')}
                                multiline
                                sx={(theme) => ({
                                    borderBottom: 'groove',
                                    '& .MuiInputBase-input': {
                                        border: 'none',
                                        background: 'none'
                                    },
                                    '& .MuiInputBase-input:focus': {
                                        borderColor: 'currentcolor !important'
                                    },
                                    '& .MuiFilledInput-input': {
                                        fontSize: theme.typography.body2.fontSize,
                                        color: 'black !important'
                                    }
                                })}
                                variant="filled"
                                {...noteField}
                            />
                        )}
                    </Stack>
                )}
            </Stack>

            <Box
                sx={{
                    backgroundColor: 'white',
                    display: 'flex',
                    flexDirection: 'column',
                    marginBottom: 8,
                    marginTop: 2,
                    padding: 2
                }}
            >
                {source_id && (
                    <div>
                        <Typography sx={{ color: 'black', fontWeight: 'medium' }} variant="subtitle1">
                            {_t('mobile_mobileform_follow_up_actions_navigate_to_source_title')}
                        </Typography>
                        <Button
                            fullWidth
                            onClick={onGoToSource}
                            sx={{ marginTop: 2, padding: 1, textTransform: 'none' }}
                            variant="outlined"
                        >
                            {_t('mobile_mobileform_follow_up_actions_navigate_to_source')}
                        </Button>
                    </div>
                )}
            </Box>
        </>
    );
};
